'use client';

import { useEffect } from 'react';

/**
 * Hook to manage favicon changes based on notification state
 * Persists across page navigations by using MutationObserver to detect and reapply changes
 * @param hasUnreadNotifications - Whether there are unread notifications
 */
export function useFaviconNotification(hasUnreadNotifications: boolean) {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Function to apply favicon changes
    const applyFaviconChanges = () => {
      const faviconLinks = document.querySelectorAll<HTMLLinkElement>('link[rel*="icon"]');

      if (hasUnreadNotifications) {
        // Store original favicons if not already stored
        faviconLinks.forEach((link) => {
          if (!link.dataset.originalHref) {
            link.dataset.originalHref = link.href;
          }
        });

        // Replace the favicon path with notification version
        faviconLinks.forEach((link) => {
          const originalHref = link.dataset.originalHref || link.href;

          // e.g., /icons/favicon.ico -> /icons/favicon-notification.ico
          // e.g., /icons/favicon-32x32.png -> /icons/favicon-notification-32x32.png
          const notificationHref = originalHref.replace(/\/icons\/(favicon(-\d+x\d+)?)(\.png|\.ico)/, '/icons/$1-notification$3');

          if (link.href !== notificationHref) {
            link.href = notificationHref;
          }
        });
      } else {
        // Restore original favicons
        faviconLinks.forEach((link) => {
          if (link.dataset.originalHref && link.href !== link.dataset.originalHref) {
            link.href = link.dataset.originalHref;
          }
        });
      }
    };

    // Apply changes immediately
    applyFaviconChanges();

    // Create a MutationObserver to watch for changes to the head element
    // This ensures our favicon changes persist even when Next.js updates the metadata
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const observer = new MutationObserver((mutations) => {
      let shouldReapply = false;

      for (const mutation of mutations) {
        // Check if any favicon links were added or modified
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLLinkElement && node.rel.includes('icon')) {
              shouldReapply = true;
            }
          });
        } else if (mutation.type === 'attributes' && mutation.target instanceof HTMLLinkElement) {
          const link = mutation.target;
          if (link.rel.includes('icon') && mutation.attributeName === 'href') {
            shouldReapply = true;
          }
        }

        // Break early if we already know we need to reapply
        if (shouldReapply) break;
      }

      if (shouldReapply) {
        // Clear any pending timeout to avoid multiple rapid updates
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Reapply our favicon changes after a small delay to let Next.js finish its updates
        timeoutId = setTimeout(() => {
          applyFaviconChanges();
          timeoutId = null;
        }, 10);
      }
    });

    // Observe the head element for changes
    observer.observe(document.head, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });

    // Cleanup observer on unmount
    return () => {
      observer.disconnect();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasUnreadNotifications]);
}
